// What a card says about whose turn it is.
//
// The server decides the turn (backend turn.ts) so the board and the panel
// cannot disagree; this only puts it into words. Two labels, worded the way
// the front desk says them, and an age coarse enough to be honest: nobody
// needs to know a patient has waited 47 minutes rather than 45.

export interface Turn {
  who: 'office' | 'patient' | null;
  since: string | Date | null;
  /** Set when nobody is waiting because the patient's last word was a thank-you. */
  reason?: 'acknowledged';
}

export interface TurnChip {
  label: string;
  tone: 'office' | 'patient' | 'closed';
  /** A day or more: the one thing on a busy board that deserves colour. */
  aged: boolean;
}

// Most open cards are the office's turn — that is what an inbox is — so
// the words stay quiet and the age carries the weight. A day is the line:
// the same line the follow-up date uses.
const AGED_AFTER_MS = 24 * 60 * 60 * 1000;

const elapsed = (since: Turn['since'], now: Date): number | null => {
  if (!since) return null;
  const ms = now.getTime() - new Date(since).getTime();
  return Number.isNaN(ms) || ms < 0 ? null : ms;
};

const age = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export function turnChip(turn: Turn | null | undefined, now: Date = new Date()): TurnChip | null {
  if (!turn) return null;
  const ms = elapsed(turn.since, now);
  // The loop is closed: say so, quietly, so the office knows Complete is safe.
  if (!turn.who) {
    if (turn.reason !== 'acknowledged') return null;
    return { label: ms === null ? 'Patient acknowledged' : `Patient acknowledged · ${age(ms)}`, tone: 'closed', aged: false };
  }
  const base = turn.who === 'office' ? 'Needs your reply' : 'Waiting on patient';
  return {
    label: ms === null ? base : `${base} · ${age(ms)}`,
    tone: turn.who,
    aged: ms !== null && ms >= AGED_AFTER_MS,
  };
}
